// src/MaintenanceHub.js
//
// Internal "Maintenance" hub for the field/office team.
// Lives at /maintenance with three nested pages:
//   /maintenance/schedule   -> MaintenanceSchedule (list view)
//   /maintenance/calendar   -> MaintenanceSchedule (calendar view)
//   /maintenance/analytics  -> MaintenanceAnalytics (punctuality / pace)
//
// The hub is intentionally minimal: a left sidebar (reusing the dashboard's
// Sidebar component with custom tabs) and an <Outlet/> for the active page.

import React, { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaCalendarAlt, FaRegCalendar, FaChartLine, FaUsersCog, FaStar, FaDollarSign, FaBuilding, FaUserCheck, FaLeaf, FaBullseye, FaTools, FaChartPie, FaFileContract, FaHandshake, FaSearch, FaFolderOpen, FaTruck } from 'react-icons/fa';
import Sidebar from './Components/Sidebar/Sidebar';
import {
  CAT_INSUITE,
  CAT_SERVICE,
  CAT_ADMIN,
  CAT_FINANCE,
  CAT_DISCOVERY,
  readMaintenanceUser,
} from './maintenanceAccess';
import { useMaintenanceAccess } from './MaintenanceAccessContext';

// Hub sidebar. Grouped into product categories — each category gets a
// `kind: 'header'` row, sub-tabs are marked `indent: true` so the sidebar
// renders them tightly under their header. When we add other internal
// product surfaces (Mechanical Room ops, Dryer Vent ops, etc.) they'll
// follow the same pattern.
//
// Every entry carries a `category` field so we can filter the whole list
// by the current user's role (maintenance-L1 / L2 / L3). Headers carry
// their own category and disappear automatically when none of their
// child tabs survive the filter.
const HUB_TABS = [
  { key: 'home', label: 'Daily Overview', icon: <FaHome />, category: CAT_INSUITE },
  { kind: 'header', key: 'cat-insuite', label: 'In-Suite Maintenance', category: CAT_INSUITE },
  { key: 'in-suite', label: 'Overview', icon: <FaBuilding />, indent: true, category: CAT_INSUITE },
  { key: 'check-ins', label: 'Daily Check In', icon: <FaUserCheck />, indent: true, category: CAT_INSUITE },
  { key: 'schedule', label: 'Schedule', icon: <FaCalendarAlt />, indent: true, category: CAT_INSUITE },
  { key: 'seasonal-planning', label: 'Seasonal Planner', icon: <FaLeaf />, indent: true, category: CAT_INSUITE },
  { key: 'calendar', label: 'Calendar', icon: <FaRegCalendar />, indent: true, category: CAT_INSUITE },
  { key: 'analytics', label: 'Performance', icon: <FaChartLine />, indent: true, category: CAT_INSUITE },
  // Under the in-suite heading, but CAT_SERVICE — portfolio-wide contract
  // and add-on revenue is L3-only.
  { key: 'contract-revenue', label: 'Contract Revenue', icon: <FaFileContract />, indent: true, category: CAT_SERVICE },
  { kind: 'header', key: 'cat-mechanical', label: 'Mechanical Maintenance', category: CAT_INSUITE },
  // Back-office view of the tech /mech-schedule flow. Same visibility
  // as the Service Tracker: every tier (L1+).
  { key: 'mech-management', label: 'Overview', icon: <FaTools />, indent: true, category: CAT_INSUITE },
  { key: 'mech-calendar', label: 'Calendar', icon: <FaRegCalendar />, indent: true, category: CAT_INSUITE },
  // Money uses the existing L3 permission for company-wide reporting.
  { kind: 'header', key: 'cat-money', label: 'Money', category: CAT_SERVICE },
  { key: 'money-overview', label: 'Overview', icon: <FaChartPie />, indent: true, category: CAT_SERVICE },
  { key: 'pnl-calendar', label: 'PnL Calendar', icon: <FaDollarSign />, indent: true, category: CAT_SERVICE },
  { key: 'building-heatmap', label: 'Building Heatmap', icon: <FaBuilding />, indent: true, category: CAT_SERVICE },
  // Division Margins remains routable and permission-managed; hidden only here.
  // Growth groups pages by purpose; each child retains its own access level.
  // CAT_DISCOVERY remains the L3 permission for the Building Index.
  { kind: 'header', key: 'cat-growth', label: 'Growth', category: CAT_DISCOVERY },
  { key: 'reviews', label: 'Reviews', icon: <FaStar />, indent: true, category: CAT_ADMIN },
  { key: 'quota-tracker', label: 'Service Tracker', icon: <FaBullseye />, indent: true, category: CAT_INSUITE },
  { key: 'building-index', label: 'Building Index', icon: <FaSearch />, indent: true, category: CAT_DISCOVERY },
  // Commission rates and payouts remain L3-only within Growth.
  { key: 'affiliate-payouts', label: 'Affiliate Marketing', icon: <FaHandshake />, indent: true, category: CAT_FINANCE },
  { kind: 'header', key: 'cat-admin', label: 'Admin', category: CAT_ADMIN },
  { key: 'manage-accounts', label: 'Manage Accounts', icon: <FaUsersCog />, indent: true, category: CAT_ADMIN },
  { key: 'documents', label: 'Documents', icon: <FaFolderOpen />, indent: true, category: CAT_ADMIN },
  { key: 'crew-fleet', label: 'Crew & Fleet', icon: <FaTruck />, indent: true, category: CAT_ADMIN },
];

// Filter the tab list to only the categories this role can see. We
// keep a header only if at least one of its children survives — that
// way an L1 user never sees an orphaned "Money" / "Admin" label.
//
// Strategy: walk the list keeping the most recent header in
// `pendingHeader`. When we hit the first surviving child under it,
// flush the header into the output and clear the pending slot (so it
// can't be pushed again when later children appear). Headers whose
// children were all filtered out never get flushed. Visibility is
// decided per child, so Growth can include the L1 Service Tracker,
// L2 Reviews, and L3 Building Index.
export function filterTabsForAccess(allTabs, canViewTab) {
  const out = [];
  let pendingHeader = null;
  for (const t of allTabs) {
    if (t.kind === 'header') {
      pendingHeader = t;
      continue;
    }
    if (!canViewTab(t.key)) continue;
    if (pendingHeader) {
      out.push(pendingHeader);
      pendingHeader = null;
    }
    out.push(t);
  }
  return out;
}

// Width of the fixed sidebar — must match Sidebar's expandedWidth so the
// main content area lines up exactly.
const SIDEBAR_WIDTH = 240;

export default function MaintenanceHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const access = useMaintenanceAccess();
  const user = { ...readMaintenanceUser(), role: access.role };

  // Derive the active tab from the current path. /maintenance (or any URL
  // that doesn't include a recognized sub-path) maps to "home".
  const activeKey = useMemo(() => {
    const parts = location.pathname.replace(/\/+$/, '').split('/');
    // parts: ['', 'maintenance', '<key?>']
    return parts[2] || 'home';
  }, [location.pathname]);

  // Re-derive the visible tab list whenever the role can change (login,
  // refresh-token swap, etc.). The user object is small so this is cheap.
  const visibleTabs = useMemo(() => {
    return filterTabsForAccess(HUB_TABS, access.canViewTab);
  }, [access.canViewTab]);

  return (
    <div style={{ minHeight: '100vh', background: '#F4F7FB' }}>
      <Sidebar
        tabs={visibleTabs}
        activeKey={activeKey}
        onNavigate={(key) => navigate(key === 'home' ? '/maintenance' : `/maintenance/${key}`)}
        collapsed={false}
        showLogout
        hubLayout
        internalUser={user}
        defaultCollapsedSections={['cat-money', 'cat-growth', 'cat-admin']}
      />
      <div className="dashboard-content dashboard-content--internal" style={{ marginLeft: SIDEBAR_WIDTH, minHeight: '100vh' }}>
        <Outlet />
      </div>
    </div>
  );
}
